import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Select } from '@/components/ui/Field';
import { Banner, EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback';
import { PageHeader } from '@/app/PlaceholderPage';
import { usePeriod } from '@/app/PeriodSwitcher';
import { databaseApi } from '@/features/database/api';
import { instrumentApi } from '@/features/instruments/api';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useAuth } from '@/features/auth/AuthContext';
import {
  STATUS_LABEL,
  assessmentApi,
  type AssessmentListItem,
  type AssessmentStatus,
} from './api';

type Scope = 'tbm' | 'individual' | 'composite';

const TITLE: Record<Scope, string> = {
  tbm: 'Indeks TBM Berdaya',
  individual: 'Kompetensi PELITA SLI',
  composite: 'Capaian Program',
};

/** L-07 — Daftar penilaian per lingkup, plus pemicu membuka penilaian baru. */
export function AssessmentListPage({ scope }: { scope: Scope }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { periods, current, currentId, isAll } = usePeriod();
  const [opening, setOpening] = useState(false);
  const [subjectId, setSubjectId] = useState('');
  const [targetPeriodId, setTargetPeriodId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const activePeriodId = targetPeriodId || (current?.id ?? periods[0]?.id ?? '');

  /** Aksi yang sedang menunggu konfirmasi. null = dialog tertutup. */
  const [pending, setPending] = useState<{ row: AssessmentListItem; mode: 'reset' | 'hapus' } | null>(
    null,
  );

  const { data, isLoading, error: loadError, refetch } = useQuery({
    queryKey: ['assessments', currentId],
    queryFn: () => assessmentApi.list({ periodId: isAll ? undefined : (current?.id ?? undefined) }),
    enabled: true,
  });

  const { data: instruments } = useQuery({
    queryKey: ['instruments'],
    queryFn: () => instrumentApi.list(),
  });

  // Subjek yang bisa dinilai: TBM untuk instrumen TBM & komposit, individu
  // untuk instrumen kompetensi.
  const subjectType: 'tbm' | 'individual' = scope === 'individual' ? 'individual' : 'tbm';

  const { data: subjects } = useQuery<{ id: string; code: string; name: string }[]>({
    queryKey: ['assessment-subjects', subjectType],
    queryFn: () =>
      subjectType === 'tbm'
        ? databaseApi.listTbm({ page: 1 }).then((r) => r.data.map((t) => ({ id: t.id, code: t.code, name: t.name })))
        : databaseApi
            .listIndividuals({ page: 1 })
            .then((r) => r.data.map((i) => ({ id: i.id, code: i.code, name: i.name }))),
  });

  const instrument = (instruments ?? []).find((i) => i.scope === scope);
  const rows = (data ?? []).filter((r) => r.instrumentCode === instrument?.code);

  const open = useMutation({
    mutationFn: () =>
      assessmentApi.open({
        instrumentId: instrument!.id,
        periodId: activePeriodId,
        subjectType,
        subjectId,
      }),
    onSuccess: (res) => {
      if (res.staleVersion) {
        // Bukan galat, tapi harus terlihat: yang dibuka memakai versi instrumen
        // yang sudah tidak terbit lagi. Menghapusnya lalu membuka lagi akan
        // memakai versi terbaru — dengan konsekuensi isian lama hilang.
        setNotice(
          'Penilaian ini sudah pernah dibuka memakai versi instrumen yang lebih lama, dan itu yang dilanjutkan. Untuk memakai versi terbaru, hapus penilaiannya lalu buka lagi.',
        );
      }
      navigate(`/pengukuran/${res.id}`);
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Gagal membuka penilaian.'),
  });

  const mutate = useMutation({
    // Hasil keduanya tidak dipakai — yang penting hanya berhasil atau tidak —
    // jadi disatukan sebagai void ketimbang memaksakan tipe gabungan.
    mutationFn: async ({ row, mode }: { row: AssessmentListItem; mode: 'reset' | 'hapus' }) => {
      if (mode === 'reset') await assessmentApi.reset(row.id);
      else await assessmentApi.remove(row.id);
    },
    onSuccess: async (_res, vars) => {
      await queryClient.invalidateQueries({ queryKey: ['assessments'] });
      // Analitik ikut disegarkan: menghapus penilaian terverifikasi mengubah
      // rata-rata dan peringkat di Beranda seketika.
      await queryClient.invalidateQueries({ queryKey: ['analytics'] });
      refetch();
      setNotice(
        vars.mode === 'reset'
          ? 'Isian dikosongkan. Penilaian kembali berstatus draf; lampiran bukti tidak ikut terhapus.'
          : 'Penilaian dihapus.',
      );
      setPending(null);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Aksi gagal dijalankan.');
      setPending(null);
    },
  });

  /** Draf bebas dibereskan; yang sudah terkirim hanya oleh super admin. */
  const canMutate = (row: AssessmentListItem) =>
    row.status === 'draft' || user?.role === 'super_admin';

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={TITLE[scope]}
        description={
          instrument
            ? `Memakai ${instrument.name} v${instrument.version} · ${isAll ? 'Semua Semester' : `periode ${current?.name ?? '—'}`}`
            : 'Instrumen untuk lingkup ini belum tersedia.'
        }
        action={
          instrument &&
          (current || isAll) && (
            <Button
              onClick={() => {
                setTargetPeriodId(current?.id ?? periods[0]?.id ?? '');
                setOpening((v) => !v);
              }}
            >
              + Penilaian Baru
            </Button>
          )
        }
      />

      {error && <Banner tone="danger">{error}</Banner>}
      {notice && <Banner tone="info">{notice}</Banner>}

      {opening && instrument && (
        <Card>
          <h2 className="mb-3 text-[17px] font-semibold">Pilih subjek</h2>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-0 flex-1 sm:max-w-xs">
              <Select
                label="Pilih Semester"
                value={activePeriodId}
                onChange={(e) => setTargetPeriodId(e.target.value)}
              >
                <option value="">Pilih semester…</option>
                {periods.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="min-w-0 flex-1 sm:max-w-md">
              <Select
                label={subjectType === 'tbm' ? 'TBM' : 'Individu PELITA SLI'}
                value={subjectId}
                onChange={(e) => setSubjectId(e.target.value)}
              >
                <option value="">Pilih…</option>
                {(subjects ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.code} — {s.name}
                  </option>
                ))}
              </Select>
            </div>
            <Button
              onClick={() => open.mutate()}
              loading={open.isPending}
              disabled={!subjectId || !activePeriodId}
            >
              Buka
            </Button>
          </div>
          <p className="mt-3 text-[13px] text-ink-secondary">
            Kalau penilaian untuk subjek dan periode ini sudah pernah dibuka, yang lama akan
            dilanjutkan — bukan dibuat kembar.
          </p>
        </Card>
      )}

      {loadError ? (
        <ErrorState message={(loadError as Error).message} onRetry={() => refetch()} />
      ) : isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : rows.length === 0 ? (
        <Card padded={false}>
          <EmptyState
            icon="📝"
            title="Belum ada penilaian di periode ini"
            description="Klik + Penilaian Baru untuk mulai menilai."
          />
        </Card>
      ) : (
        <Card padded={false} className="min-w-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-left">
              <thead>
                <tr className="border-b border-border">
                  <Th>Subjek</Th>
                  <Th className="w-36">Periode</Th>
                  <Th className="w-28">Angkatan</Th>
                  <Th className="w-24">Indeks</Th>
                  <Th className="w-36">Kategori</Th>
                  <Th className="w-32">Status</Th>
                  <Th className="w-28">Aksi</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-border align-top last:border-0">
                    <Td>
                      {row.subjectType === 'individual' || scope === 'individual' ? (
                        <div className="flex flex-col">
                          <span className="font-medium text-ink">
                            {row.individualCode ? `${row.individualCode} — ` : ''}
                            {row.individualName ?? '—'}
                          </span>
                          {row.tbmName && (
                            <span className="text-xs text-ink-secondary">
                              {row.tbmCode ? `${row.tbmCode} · ` : ''}
                              {row.tbmName}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="font-medium text-ink">
                          {row.tbmCode ? `${row.tbmCode} — ` : ''}
                          {row.tbmName ?? '—'}
                        </span>
                      )}
                    </Td>
                    <Td>
                      <span className="text-xs font-medium text-ink-secondary">
                        {row.periodName ?? '—'}
                      </span>
                    </Td>
                    <Td>
                      <span className="tabular">{row.angkatan ?? '—'}</span>
                    </Td>
                    <Td>
                      <span className="tabular font-semibold text-ink">
                        {row.totalScore === null ? '—' : Math.round(row.totalScore * 100) / 100}
                      </span>
                    </Td>
                    <Td>{row.band ?? <span className="text-ink-muted">belum lengkap</span>}</Td>
                    <Td>
                      <StatusChip status={row.status} />
                    </Td>
                    <Td>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => navigate(`/pengukuran/${row.id}`)}
                          title="Buka penilaian"
                          aria-label="Buka penilaian"
                          className="inline-flex size-8 items-center justify-center rounded-lg text-ink-secondary transition-colors hover:bg-primary-soft hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 cursor-pointer"
                        >
                          <EyeIcon className="size-[18px]" />
                        </button>
                        {(row.status !== 'verified' || user?.role === 'super_admin') && (
                          <button
                            type="button"
                            onClick={() => navigate(`/pengukuran/${row.id}?edit=true`)}
                            title="Ubah / Edit penilaian"
                            aria-label="Ubah penilaian"
                            className="inline-flex size-8 items-center justify-center rounded-lg text-ink-secondary transition-colors hover:bg-primary-soft hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 cursor-pointer"
                          >
                            <PencilIcon className="size-[18px]" />
                          </button>
                        )}
                        {canMutate(row) && (
                          <>
                            <button
                              type="button"
                              onClick={() => setPending({ row, mode: 'reset' })}
                              title="Reset penilaian"
                              aria-label="Reset penilaian"
                              className="inline-flex size-8 items-center justify-center rounded-lg text-ink-secondary transition-colors hover:bg-warn-soft hover:text-[#9a6400] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warn/50 cursor-pointer"
                            >
                              <RotateCcwIcon className="size-[18px]" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setPending({ row, mode: 'hapus' })}
                              title="Hapus penilaian"
                              aria-label="Hapus penilaian"
                              className="inline-flex size-8 items-center justify-center rounded-lg text-ink-secondary transition-colors hover:bg-danger-soft hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger/50 cursor-pointer"
                            >
                              <TrashIcon className="size-[18px]" />
                            </button>
                          </>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ConfirmDialog
        open={pending !== null}
        title={pending?.mode === 'reset' ? 'Kosongkan isian penilaian?' : 'Hapus penilaian?'}
        confirmLabel={pending?.mode === 'reset' ? 'Kosongkan' : 'Hapus'}
        loading={mutate.isPending}
        onConfirm={() => pending && mutate.mutate(pending)}
        onCancel={() => setPending(null)}
      >
        {pending && (
          <div className="flex flex-col gap-2 text-ink-secondary">
            <p>
              <strong className="text-ink">
                {pending.row.individualName
                  ? `${pending.row.individualCode ? `${pending.row.individualCode} — ` : ''}${pending.row.individualName}`
                  : `${pending.row.tbmCode ? `${pending.row.tbmCode} — ` : ''}${pending.row.tbmName ?? 'Penilaian ini'}`}
              </strong>{' '}
              · Periode: <strong>{pending.row.periodName ?? '—'}</strong> · {STATUS_LABEL[pending.row.status]}
              {pending.row.totalScore !== null && (
                <> · indeks {Math.round(pending.row.totalScore * 100) / 100}</>
              )}
            </p>
            <p>
              {pending.mode === 'reset'
                ? 'Seluruh skor dan catatan dikosongkan, dan penilaian kembali jadi draf. Lampiran bukti yang sudah diunggah TIDAK ikut terhapus.'
                : 'Penilaian beserta seluruh skor dan lampiran buktinya dihapus permanen. Subjeknya bisa dinilai ulang dari nol setelah ini.'}
            </p>
            {pending.row.status !== 'draft' && (
              <p className="text-[13px] text-danger">
                Penilaian ini sudah {STATUS_LABEL[pending.row.status].toLowerCase()} dan angkanya
                sudah masuk ke Beranda serta Rapor. Menghapusnya menggeser rata-rata dan peringkat.
              </p>
            )}
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
}

function StatusChip({ status }: { status: AssessmentStatus }) {
  return (
    <span
      className={cn(
        'rounded-full px-2.5 py-0.5 text-[12px] font-medium',
        status === 'verified'
          ? 'bg-success-soft text-success'
          : status === 'submitted'
            ? 'bg-primary-soft text-primary'
            : status === 'returned'
              ? 'bg-danger-soft text-danger'
              : 'bg-warn-soft text-[#9a6400]',
      )}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn('px-4 py-3 text-[13px] font-semibold text-ink-secondary', className)}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-4 py-3.5 align-top">{children}</td>;
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
      <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  );
}

function RotateCcwIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

